import React, { useCallback, useState } from 'react';
import { GitCompare } from 'lucide-react';
import toolkitService from '../../services/ToolkitService';
import Button from '../shared/Button';
import { useToolkitProject, ToolkitProjectHeader } from './ToolkitProject';
import ToolkitFileStep, { STEP, stepIcon } from './ToolkitFileStep';
import ToolkitCompareResults from './ToolkitCompareResults';
import '../OhipTransactionsPage.css';
import '../ToolkitBlocksPage.css';

// A v5 report against the same report from Opera Cloud — the shape of the Toolkit's balance checks (Reservations,
// History & Forecast, AR Ledger). Both files go to S3; the server parses and compares them (compare.js `kind`) and
// returns the summary and every row that is not a clean match. Read-only: nothing is sent to Opera Cloud.
//
// A screen supplies its words, `kind`, the result `columns` / `statuses`, `summaryOf(summary)` → cards, optional
// `options` (rendered with the Compare button) and `optionValues` (sent with the request), and optional
// `children(result)` — e.g. actions on the flagged rows.

const uploaded = async (key) => ({ data: key, message: 'Uploaded — compared when you press Compare.' });

function ToolkitComparePage({ title, subtitle, kind, v5Title, cloudTitle, v5Label, cloudLabel, accept, columns, statuses,
                              summaryOf, options, optionValues, exportName, children }) {
    const toolkit = useToolkitProject();
    const { ctx, ready } = toolkit;
    const [v5Key, setV5Key] = useState(null);
    const [cloudKey, setCloudKey] = useState(null);
    const [run, setRun] = useState({ state: STEP.IDLE, message: '', result: null });
    const onV5 = useCallback((key) => { setV5Key(key); setRun({ state: STEP.IDLE, message: '', result: null }); }, []);
    const onCloud = useCallback((key) => { setCloudKey(key); setRun({ state: STEP.IDLE, message: '', result: null }); }, []);

    const doCompare = async () => {
        setRun({ state: STEP.BUSY, message: 'Comparing…', result: null });
        try {
            const { data } = await toolkitService.compare(ctx, kind, v5Key, cloudKey, optionValues || {});
            setRun({ state: STEP.DONE, result: data,
                message: data.truncated ? `Showing the first ${data.rows.length} rows to look at; ${data.truncated} more are not listed — export and narrow the reports.` : '' });
        } catch (err) {
            setRun({ state: STEP.ERROR, result: null, message: err?.response?.data?.message || err?.response?.data?.error?.message || err.message });
        }
    };

    return (
        <div className="otx-page">
            <ToolkitProjectHeader toolkit={toolkit} title={title} subtitle={subtitle} />
            <ToolkitFileStep number={1} title={v5Title} label={v5Label} accept={accept} parse={uploaded} onParsed={onV5} disabled={!ready} />
            <ToolkitFileStep number={2} title={cloudTitle} label={cloudLabel} accept={accept} parse={uploaded} onParsed={onCloud} disabled={!ready} />
            <div className="tkb-step">
                <div className="tkb-step-head"><span className="tkb-num">3</span> Compare {stepIcon(run.state)}</div>
                <div className="tkb-row">
                    {options}
                    <Button variant="success" onClick={doCompare} disabled={!ready || !v5Key || !cloudKey || run.state === STEP.BUSY}>
                        <GitCompare className="icon" /> Compare
                    </Button>
                </div>
                {run.message && <p className={`tkb-msg${run.state === STEP.ERROR ? ' tkb-fail' : ''}`}>{run.message}</p>}
            </div>
            {run.result && (
                <>
                    <ToolkitCompareResults summary={summaryOf(run.result.summary)} rows={run.result.rows} columns={columns}
                        statuses={statuses} exportName={exportName}
                        note="Matches are counted, not listed — the grid shows only what needs a look." />
                    {children && children(run.result)}
                </>
            )}
        </div>
    );
}

export default ToolkitComparePage;
